import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useToast } from '../contexts/ToastContext'
import { groups } from '../lib/api'
import { useGroupDetail, useGroupJoinRequests, useInvalidateGroups } from '../hooks/useGroups'
import { Card } from '../components/ui/Card'
import { StatBlock } from '../components/ui/StatBlock'
import { Avatar } from '../components/ui/Avatar'
import { CopyButton } from '../components/ui/CopyButton'
import { Input } from '../components/ui/Input'
import { Button } from '../components/ui/Button'
import { EmptyState } from '../components/ui/EmptyState'
import { cn } from '../lib/cn'
import type { GroupCircleDto, GroupMemberDto } from '../types/community'
import { usePerson } from '../contexts/PersonContext'
import { useActionBoundary } from '../hooks/useActionBoundary'
import { tryCopyText } from '../lib/clipboard'

function tintFromId(id: string): number {
  let sum = 0
  for (let i = 0; i < id.length; i++) sum += id.charCodeAt(i)
  return sum % 6
}

function initials(name: string): string {
  const s = (name || '').trim()
  return s ? s.slice(0, 2).toUpperCase() : '?'
}

export function GroupPage() {
  const { id } = useParams<{ id: string }>()
  const { flash } = useToast()
  const invalidate = useInvalidateGroups()
  const navigate = useNavigate()
  const { role, flags, guardianFirst, activePerson } = usePerson()

  const capture = useActionBoundary(id)
  const dirty = useRef(false)

  const detailQuery = useGroupDetail(id)
  const { data: detail, isLoading } = detailQuery
  const isHost = role === 'parent' && detail?.myRole === 'Host'
  const isMember = detail?.isMember === true
  const requestsQuery = useGroupJoinRequests(id, isHost)
  const joinRequests = requestsQuery.data ?? []
  const [operations, setOperations] = useState<Record<string, boolean>>({})
  const [operationError, setOperationError] = useState<string | null>(null)
  const inFlight = useRef(new Set<string>())
  const [confirmLeave, setConfirmLeave] = useState(false)
  const [manualCopy, setManualCopy] = useState<string | null>(null)
  const [joinRequested, setJoinRequested] = useState(false)

  async function operation(key: string, action: (current: () => boolean) => Promise<void>) {
    if (inFlight.current.has(key)) return
    const current = capture()
    inFlight.current.add(key); setOperations((old) => ({ ...old, [key]: true })); setOperationError(null)
    try { await action(current) }
    catch (err) { if (current()) setOperationError(err instanceof Error ? err.message : 'That did not go through. Try again.') }
    finally { inFlight.current.delete(key); if (current()) setOperations((old) => ({ ...old, [key]: false })) }
  }

  // Profile editor mirrors the loaded profile.
  const [handleInGame, setHandleInGame] = useState('')
  const [likes, setLikes] = useState('')
  const [plays, setPlays] = useState('')
  const [savingProfile, setSavingProfile] = useState(false)

  useEffect(() => { dirty.current = false; inFlight.current.clear(); setOperations({}); setOperationError(null); setConfirmLeave(false); setManualCopy(null); setJoinRequested(false); setSavingProfile(false) }, [id, activePerson?.id])
  useEffect(() => {
    if (detail && !dirty.current) {
      setHandleInGame(detail.myProfile?.handleInGame ?? '')
      setLikes(detail.myProfile?.likes ?? '')
      setPlays(detail.myProfile?.plays ?? '')
    }
  }, [detail])

  if (isLoading) {
    return <div className="animate-rise px-10 py-9 font-mono text-[11px] uppercase tracking-label text-ink-faint">Loading…</div>
  }
  if (!detail || !id) {
    return (
      <div className="animate-rise px-10 py-9">
        <EmptyState
          dashed
          title="This group could not be opened"
          description="It may be unavailable or your connection may be interrupted. Try again."
          action={<Button variant="outline" onClick={() => void detailQuery.refetch()}>Retry group</Button>}
        />
      </div>
    )
  }

  async function toggleCircle(circle: GroupCircleDto, current: () => boolean) {
    if (!isHost) {
      flash('Only the group host can change which circles are in it.')
      return
    }
      if (circle.attached) {
        await groups.detachCircle(id as string, circle.id)
        if (!current()) return
        flash(`${circle.name} removed from this group's circles — nobody loses their place.`)
      } else {
        await groups.attachCircle(id as string, circle.id)
        if (!current()) return
        flash(`${circle.name} added to this group's circles — people in it can find and join.`)
      }
      invalidate(id)
  }

  async function copyAllUsernames() {
    const current = capture()
    const handles = detail!.members.map((m) => m.handleInGame).filter((h) => h && h.trim().length > 0)
    const text = handles.join('\n')
    const done = () => flash(`${handles.length} username${handles.length === 1 ? '' : 's'} copied — paste it into the game.`)
    const copied = await tryCopyText(text)
    if (!current()) return
    if (copied) { setManualCopy(null); done() }
    else setManualCopy(text)
  }

  async function saveProfile() {
    if (handleInGame.trim().length === 0) {
      flash('Add your in-game username first — that is what the group is for.')
      return
    }
    if (savingProfile) return
    const current = capture()
    setSavingProfile(true)
    try {
      await groups.saveProfile(id as string, {
        handleInGame: handleInGame.trim(),
        likes: likes.trim() || undefined,
        plays: plays.trim() || undefined,
      })
      if (!current()) return
      dirty.current = false
      flash('Profile saved. Group members, guardian hosts and authorised guardian observation can see the permitted details.')
      invalidate(id)
    } catch (err) {
      if (current()) setOperationError((err as Error)?.message ?? 'Something went wrong. Please try again.')
    } finally {
      if (current()) setSavingProfile(false)
    }
  }

  async function decideRequest(reqId: string, approve: boolean, current: () => boolean) {
      if (approve) {
        await groups.approveJoinRequest(id as string, reqId)
        if (!current()) return
        flash('Approved — they are in the group now.')
      } else {
        await groups.declineJoinRequest(id as string, reqId)
        if (!current()) return
        flash('Join request declined.')
      }
      invalidate(id)
  }

  return (
    <div className="animate-rise px-5 py-6 sm:px-10 sm:py-9">
      <div className="font-mono text-[12px] uppercase tracking-label text-ink-faint">
        Group · {detail.game} · {isHost ? 'you host this group' : isMember ? 'you are a member' : 'you are viewing this group'}
      </div>
      <h1 className="mt-2 font-serif text-[32px] font-medium leading-tight tracking-[-.015em] text-ink">
        {detail.name}
      </h1>
      <p className="mt-3 max-w-[62ch] text-[15px] leading-relaxed text-ink-muted">
        Members are people who joined for themselves — not a circle's contents. Adding or removing a
        circle changes who can find and join this group; it never adds or removes a member, and
        members stay even if they later leave the circle.
      </p>
      {operationError && <p role="alert" className="mt-4 rounded-input bg-honey-bg p-3 text-sm text-honey-fg">{operationError}</p>}
      <div className="mt-5 flex flex-wrap gap-3">
        {!isMember && detail.canJoin && !flags.viewOnly && <Button disabled={operations.join || joinRequested} variant="accent" onClick={() => void operation('join', async (current) => {
          await groups.joinGroup(id, [])
          if (!current()) return
          if (role !== 'parent' || (detail.access === 'VouchNeeded' && !isHost)) { setJoinRequested(true); flash(role !== 'parent' ? `Sent to ${guardianFirst || 'your guardian'} for approval.` : 'Join request sent to the host.') }
          else flash('You joined as a player.')
          invalidate(id)
        })}>{operations.join ? 'Joining…' : joinRequested ? 'Requested' : role === 'parent' ? 'Join as a player' : 'Ask to join'}</Button>}
        {joinRequested && <Button variant="outline" disabled={operations.status} onClick={() => void operation('status', async (current) => {
          const fresh = await detailQuery.refetch()
          if (!current()) return
          if (fresh.isError) throw fresh.error ?? new Error('Request status could not be checked. Please try again.')
          setJoinRequested(false)
          flash('Group details refreshed. You can retry joining if access is available; pending requests are handled by the server.')
        })}>{operations.status ? 'Checking request…' : 'Check request status'}</Button>}
        {!isMember && detail.audience === 'KidsOnly' && role === 'parent' && <p className="text-sm text-ink-muted">This group is for kids. A guardian can host or observe without joining as a player.</p>}
        {detail.canObserve && role === 'parent' && <Link to={`/groups/${id}/observe`} className="rounded-btn border border-line px-4 py-3 text-sm text-terra-deep">Observe your managed profile</Link>}
        {isMember && <Button variant="outline" onClick={() => setConfirmLeave(true)}>Leave group</Button>}
      </div>
      {confirmLeave && <Card tone="panel" className="mt-4">
        <p className="text-sm text-ink-muted">Leave this group as your current profile? Hosting and circle invitations are separate from your player membership.</p>
        <div className="mt-3 flex flex-wrap gap-3"><Button disabled={operations.leave} onClick={() => void operation('leave', async (current) => { await groups.leaveGroup(id); if (!current()) return; invalidate(id); navigate('/browse') })}>{operations.leave ? 'Leaving…' : 'Confirm leave'}</Button><Button variant="outline" disabled={operations.leave} onClick={() => setConfirmLeave(false)}>Stay in group</Button></div>
      </Card>}

      {/* Stats */}
      <div className="mt-6 flex flex-wrap gap-x-10 gap-y-4">
        <StatBlock label="Circles in group" value={detail.stats.circles} />
        <StatBlock label="Families" value={detail.stats.families} />
        <StatBlock label="Players" value={detail.stats.players} />
        <StatBlock label="Hosted by" value={isHost ? 'You' : 'A host'} positive />
      </div>

      {/* Membership chips (own circles) */}
      <section className="mt-8">
        <div className="mb-3 font-mono text-[11px] uppercase tracking-label text-ink-faint">This group's circles</div>
        {detail.circles.length === 0 ? (
          <p className="text-[13px] text-ink-muted">No circles are shown for this group.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {detail.circles.map((circle) => (
              <button
                key={circle.id}
                type="button"
                disabled={operations[`circle:${circle.id}`]}
                onClick={() => void operation(`circle:${circle.id}`, (current) => toggleCircle(circle, current))}
                className={cn(
                  'inline-flex items-center gap-2 rounded-pill border px-[14px] py-2 text-[13px] transition-colors',
                  circle.attached
                    ? 'border-honey-fg/40 bg-honey-chip text-honey-fg'
                    : 'border-line bg-paper-surface text-ink-muted hover:border-terra',
                )}
              >
                <span className="h-[9px] w-[9px] flex-none rounded-full" style={{ background: circle.dot }} />
                <span>{circle.name}</span>
                <span className="font-mono text-[10px] text-ink-faint">{circle.familyCount} families</span>
                <span className={cn('ml-1 font-mono text-[13px] font-semibold', circle.attached ? 'text-honey-fg' : 'text-moss')}>
                  {circle.attached ? '−' : '+'}
                </span>
              </button>
            ))}
          </div>
        )}
        {isHost && detail.circles.some((circle) => circle.attached) && <div className="mt-3 flex flex-wrap gap-2">
          {detail.circles.filter((circle) => circle.attached).map((circle) => <Button key={circle.id} variant="outline" size="sm" disabled={operations[`invite:${circle.id}`]} onClick={() => void operation(`invite:${circle.id}`, async (current) => {
            const count = await groups.inviteCircle(id, circle.id)
            if (!current()) return
            flash(`${count} invitation${count === 1 ? '' : 's'} sent. Each player chooses whether to join.`)
            invalidate(id)
          })}>{operations[`invite:${circle.id}`] ? 'Inviting…' : `Invite ${circle.name} members`}</Button>)}
        </div>}
        <p className="mt-2 text-[12px] text-ink-faint">
          {isHost
            ? 'You host this group — its circles set who can find and join it. People still join for themselves.'
            : isMember ? 'Your player membership stays even if a circle changes.' : 'Each player joins or requests access for themselves.'}
        </p>
      </section>
      {isHost && requestsQuery.isError && <div role="alert" className="mt-5 text-sm text-honey-fg">Join requests could not be loaded. <Button variant="outline" onClick={() => void requestsQuery.refetch()}>Retry join requests</Button></div>}

      {/* Host: pending vouch requests */}
      {isHost && joinRequests.length > 0 && (
        <Card tone="panel" className="mt-8">
          <div className="mb-3 text-[14px] font-semibold text-ink">Player join requests</div>
          <div className="flex flex-col gap-3">
            {joinRequests.map((req) => (
              <div key={req.id} className="flex items-center justify-between gap-4 border-b border-line-row pb-3 last:border-0 last:pb-0">
                <div>
                  <div className="text-[14px] text-ink">{req.familyName}</div>
                  <div className="text-[12px] text-ink-muted">Bringing {req.circleNames} · {req.whenText}</div>
                </div>
                <div className="flex flex-none gap-2">
                  <Button variant="approve" size="sm" disabled={operations[`request:${req.id}`]} onClick={() => void operation(`request:${req.id}`, (current) => decideRequest(req.id, true, current))}>Approve</Button>
                  <Button variant="outline" size="sm" disabled={operations[`request:${req.id}`]} onClick={() => void operation(`request:${req.id}`, (current) => decideRequest(req.id, false, current))}>Not this one</Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Members + usernames */}
      <section className="mt-8">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="font-mono text-[11px] uppercase tracking-label text-ink-faint">Members & their in-game usernames</div>
          {detail.members.length > 0 && (
            <button
              type="button"
              onClick={copyAllUsernames}
              className="rounded-btn border border-line bg-paper-surface px-3 py-2 font-mono text-[11px] uppercase tracking-label text-ink-muted transition-colors hover:border-terra hover:text-terra-deep"
            >
              Copy all usernames
            </button>
          )}
        </div>
        {detail.members.length === 0 ? (
          <EmptyState
            dashed
            description="No players have joined this group yet. Circle invitations offer a place; each player chooses whether to join."
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {detail.members.map((m: GroupMemberDto) => {
              // Outsiders receive handle-only rows (no first/family name) — render the handle
              // honestly rather than a blank or a fabricated name. `handle` is not yet on the
              // lead-owned GroupMemberDto type, so read it at the call site (same pattern as isOwner).
              const handle = (m as { handle?: string }).handle ?? ''
              const label = m.firstName || handle || 'A player'
              const nameHidden = !m.firstName
              return (
              <Card key={m.personId} padding="md">
                <div className="flex items-start gap-3">
                  <Avatar initials={initials(label)} tintIndex={tintFromId(m.personId)} size={38} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[14px] font-medium text-ink">{label}</div>
                    <div className="truncate text-[12px] text-ink-muted">
                      {nameHidden
                        ? "Handle only — join to see who's here."
                        : `${m.familyName}${m.viaCircles ? ` · via ${m.viaCircles}` : ''}`}
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <span className="rounded-input border border-line bg-paper-row px-[10px] py-[6px] font-mono text-[12px] text-ink">
                        {m.handleInGame && m.handleInGame.trim().length > 0 ? m.handleInGame : '—'}
                      </span>
                      {m.handleInGame && m.handleInGame.trim().length > 0 && (
                        <CopyButton value={m.handleInGame} what="Username" />
                      )}
                    </div>
                    <div className="mt-2 text-[12px] text-ink-muted">Likes: {m.likes && m.likes.trim() ? m.likes : '—'}</div>
                    <div className="text-[12px] text-ink-muted">Plays: {m.plays && m.plays.trim() ? m.plays : '—'}</div>
                    {m.note && m.note.trim() && <div className="mt-1 text-[12px] italic text-ink-faint">{m.note}</div>}
                  </div>
                </div>
              </Card>
              )
            })}
          </div>
        )}
      </section>
      {manualCopy !== null && <div className="mt-4"><p role="alert" className="text-sm text-honey-fg">Copy was blocked. Select and copy these usernames:</p><textarea readOnly aria-label="Usernames to copy" value={manualCopy} onFocus={(event) => event.currentTarget.select()} className="mt-2 w-full rounded-input border border-line p-3 font-mono text-sm" /></div>}

      {/* My profile in this group */}
      {isMember && <Card tone="panel" className="mt-8">
        <div className="mb-1 text-[14px] font-semibold text-ink">Your profile in this group</div>
        <p className="mb-4 text-[12px] text-ink-muted">
          Visible only inside this group. No surname, no age, no contact details allowed.
        </p>
        <div className="flex flex-col gap-3">
          <Input disabled={savingProfile} label="In-game username" value={handleInGame} onChange={(e) => { dirty.current = true; setHandleInGame(e.target.value) }} placeholder="HanaBuilds" />
          <Input disabled={savingProfile} label="What you like" value={likes} onChange={(e) => { dirty.current = true; setLikes(e.target.value) }} placeholder="Redstone, big builds" />
          <Input disabled={savingProfile} label="When you play" value={plays} onChange={(e) => { dirty.current = true; setPlays(e.target.value) }} placeholder="Weekday evenings" />
          <div>
            <Button variant="accent" onClick={saveProfile} disabled={savingProfile}>
              {savingProfile ? 'Saving…' : 'Save profile'}
            </Button>
          </div>
        </div>
      </Card>}

      {/* Foot */}
      <Card className="mt-8">
        <div className="text-[13px] leading-relaxed text-ink-muted">
          Group members and guardian hosts can see shared first names, family displays, circle connections and group profile details. Authorised guardians can observe the permitted details for their managed profiles.
        </div>
      </Card>
    </div>
  )
}
